// 하나 바이블 진도: 저장값 "부서:과정-학기:과[:기준 주일]" 해석과 매주 자동 진행
// (백엔드 BE-0a 로 기준 주일 저장 허용 — docs/chatbot-work-plan.md F1·F2·F7)
// 정규 공과는 1과정 1학기 1과 → … → 6과정 2학기 44과(264개) 순서로 돌고, 다 끝나면 1과정 1학기 1과로 돌아간다.
// 공과 데이터 없이 계산만으로 쓰므로 진도를 다루는 모든 화면에서 같이 쓴다.

const TOTAL_LESSONS = 264;
const LESSONS_PER_SEMESTER = 22;
const DAY_MS = 24 * 60 * 60 * 1000;

const pad = (n) => String(n).padStart(2, "0");
export const toDateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromDateKey = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
};

// 이번 주 수업 주일: 오늘이 주일이면 오늘, 아니면 다가오는 주일 (교사는 주중에 다가오는 주일 수업을 준비한다)
export const upcomingSunday = (today = new Date()) => {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  d.setDate(d.getDate() + ((7 - d.getDay()) % 7));
  return toDateKey(d);
};

export const addWeeks = (dateKey, weeks) => {
  const d = fromDateKey(dateKey);
  d.setDate(d.getDate() + weeks * 7);
  return toDateKey(d);
};

const weeksBetween = (fromKey, toKey) => Math.round((fromDateKey(toKey) - fromDateKey(fromKey)) / (7 * DAY_MS));

// "6-2", 35 → 0~263 (정규 공과가 아니면 -1)
export const lessonIndex = (semesterKey, lesson) => {
  const [course, semester] = String(semesterKey || "").split("-").map(Number);
  const n = Number(lesson);
  if (!(course >= 1 && course <= 6 && (semester === 1 || semester === 2))) return -1;
  const first = (semester - 1) * LESSONS_PER_SEMESTER + 1;
  if (!(n >= first && n < first + LESSONS_PER_SEMESTER)) return -1;
  return ((course - 1) * 2 + (semester - 1)) * LESSONS_PER_SEMESTER + (n - first);
};

// 0~263 → { semesterKey, lesson } (범위를 넘으면 순환)
export const lessonAt = (index) => {
  const i = ((index % TOTAL_LESSONS) + TOTAL_LESSONS) % TOTAL_LESSONS;
  const course = Math.floor(i / (LESSONS_PER_SEMESTER * 2)) + 1;
  const semester = Math.floor((i % (LESSONS_PER_SEMESTER * 2)) / LESSONS_PER_SEMESTER) + 1;
  return { semesterKey: `${course}-${semester}`, lesson: (semester - 1) * LESSONS_PER_SEMESTER + (i % LESSONS_PER_SEMESTER) + 1 };
};

// 정규 공과 순서상 몇 과 앞·뒤 (진도 버튼용)
export const shiftLesson = ({ semesterKey, lesson }, weeks) => {
  const i = lessonIndex(semesterKey, lesson);
  return i < 0 ? null : lessonAt(i + weeks);
};

// 저장값 → { group, semesterKey, lesson, anchor } (아무것도 없으면 null). "::" 처럼 빈 값도 받는다
export const parsePreference = (raw) => {
  const [group = "", semesterKey = "", lesson = "", anchor = ""] = String(raw || "").split(":");
  if (!group && !semesterKey && !lesson) return null;
  return {
    group,
    semesterKey,
    lesson: Number(lesson) || null,
    anchor: /^\d{4}-\d{2}-\d{2}$/.test(anchor) ? anchor : null,
  };
};

// { group, semesterKey, lesson, anchor } → 저장값. 기준 주일은 학기·과가 있을 때만 붙인다
export const formatPreference = ({ group = "", semesterKey = "", lesson = null, anchor = null }) => {
  const base = `${group}:${semesterKey}:${lesson ?? ""}`;
  return anchor && semesterKey && lesson ? `${base}:${anchor}` : base;
};

// 이번 주 진도: 기준 주일부터 지난 주일 수만큼 과를 넘긴다.
// 기준 주일이 없거나(예전 저장값) 아직 오지 않았으면 저장된 과 그대로. weeks 는 넘긴 주 수
export const currentProgress = (pref, today = new Date()) => {
  if (!pref) return null;
  const index = lessonIndex(pref.semesterKey, pref.lesson);
  if (index < 0 || !pref.anchor) return { ...pref, weeks: 0 };
  const weeks = weeksBetween(pref.anchor, upcomingSunday(today));
  if (weeks <= 0) return { ...pref, weeks: 0 };
  return { ...pref, ...lessonAt(index + weeks), weeks };
};

// 세 칸으로 저장하던 화면용: 부서·학기·과가 모두 있으면 이번 주를 기준 주일로 붙인다
export const appendAnchor = (value, today = new Date()) => {
  const parts = String(value).split(":");
  return parts.length === 3 && parts.every(Boolean) ? `${value}:${upcomingSunday(today)}` : value;
};
